/**
 * Elegir columnas a mano (importar extracto): nombres que se enseñan y
 * columnas de partida cuando no se ha reconocido nada.
 */
import { movimientosDeFilas } from './leerExtracto';
import type { Columnas, Lectura, MovimientoLeido } from './leerExtracto';

/** Letra de hoja de cálculo: 0 → A, 26 → AA. */
export function letraColumna(indice: number): string {
  let letras = '';
  let n = indice + 1;
  while (n > 0) {
    const resto = (n - 1) % 26;
    letras = String.fromCharCode(65 + resto) + letras;
    n = Math.floor((n - 1) / 26);
  }
  return letras;
}

/**
 * Un nombre por columna: «B · Concepto» si hay cabecera; si no, la letra y
 * un ejemplo de la primera fila completa («B · COMPRA MERCADONA»).
 */
export function nombresColumnas(
  filas: string[][],
  filaCabecera: number | null,
  t: (clave: string, opciones?: Record<string, unknown>) => string,
): string[] {
  const ancho = Math.max(0, ...filas.slice(0, 50).map(f => f.length));
  const ejemplo =
    filaCabecera != null
      ? filas[filaCabecera]
      : (filas.find(f => f.length === ancho && f.every(c => c !== '')) ??
        filas.find(f => f.length === ancho) ??
        []);
  return Array.from({ length: ancho }, (_, i) => {
    const valor = (ejemplo[i] ?? '').trim();
    return valor
      ? `${letraColumna(i)} · ${valor}`
      : t('Column {{letter}}', { letter: letraColumna(i) });
  });
}

/** Punto de partida sin nada reconocido: A, B y C. */
export function columnasIniciales(filas: string[][]): Columnas {
  const ancho = Math.max(1, ...filas.slice(0, 50).map(f => f.length));
  return {
    fecha: 0,
    concepto: Math.min(1, ancho - 1),
    importe: Math.min(2, ancho - 1),
    cargo: null,
    abono: null,
  };
}

/**
 * Columnas guardadas por cuenta para la próxima vez. Solo las columnas: la
 * fila de cabecera cambia de un extracto a otro, y las filas sin fecha ni
 * importe se descartan solas.
 */
export function leerFormatoGuardado(
  valor: string | undefined,
): Columnas | null {
  if (!valor) {
    return null;
  }
  try {
    const c = JSON.parse(valor);
    const esIndice = (v: unknown) =>
      v === null || (typeof v === 'number' && Number.isInteger(v) && v >= 0);
    if (
      !c ||
      typeof c.fecha !== 'number' ||
      typeof c.concepto !== 'number' ||
      ![c.fecha, c.concepto, c.importe, c.cargo, c.abono].every(esIndice) ||
      (c.importe == null && (c.cargo == null || c.abono == null))
    ) {
      return null;
    }
    return {
      fecha: c.fecha,
      concepto: c.concepto,
      importe: c.importe,
      cargo: c.cargo,
      abono: c.abono,
    };
  } catch {
    return null;
  }
}

/**
 * CSV o Excel: lo reconocido, o las columnas de la última vez en esta cuenta
 * si dan más movimientos.
 */
export function lecturaConGuardada(
  filas: string[][],
  reconocida: Lectura | null,
  guardadas: Columnas | null,
): { lectura: Lectura | null; movimientos: MovimientoLeido[] } {
  const deGuardadas = guardadas
    ? movimientosDeFilas(filas, { filaCabecera: null, columnas: guardadas })
        .movimientos
    : [];
  const reconocidos = reconocida
    ? movimientosDeFilas(filas, reconocida).movimientos
    : [];
  if (guardadas != null && deGuardadas.length > reconocidos.length) {
    return {
      lectura: { filaCabecera: null, columnas: guardadas },
      movimientos: deGuardadas,
    };
  }
  return { lectura: reconocida, movimientos: reconocidos };
}
